interface DescriptionTemplateValues {
  readonly [parameter: string]: string
}

export { DescriptionTemplateValues }

/**
 * Stores a description of a value.
 * Provides methods for accessing the description in various ways.
 */
export default class Description {
  public readonly template: string

  /**
   * Creates a description.
   * Example inputs: "The node id was #[123]#{id} and it sent #[1234 XRP]#{amount}".
   *
   * @param template - A description string that contains the template inside it.
   */
  public constructor(template: string) {
    this.template = template
  }

  /**
   * Returns just the template from the description/template string.
   *
   * Example: "The node id was #[123]#{id} and it sent #[1234 XRP]#{amount}" => "The node id was #{id} and it sent #{amount}".
   *
   * @returns The template of the description string.
   */
  public getTemplate(): string {
    // Delete all instances of [parameter-value]
    return this.template.replace(/#\[.*?\]/gu, '')
  }

  /**
   * Returns just the description from the description/template string.
   *
   * Example: "The node id was #[123]#{id} and it sent #[1234 XRP]#{amount}" => "The node id was 123 and it sent 1234 XRP".
   *
   * @returns The description.
   */
  public getDescription(): string {
    // Delete all instances of either [ or ] or {template-parameter}
    return this.template
      .replace(/#\{.*?\}/gu, '')
      .replace(/#\[(?<parameter>.*?)\]/gu, '$1')
  }

  /**
   * Calculates the values of the template.
   *
   * Example: "The node id was #[123]#{id} and it sent #[1234 XRP]#{amount}" => { id: "123", amount: "1234 XRP" }.
   *
   * @returns A dictionary mapping template parameter name to the template value.
   */
  public getTemplateValues(): DescriptionTemplateValues {
    const templateValues: { [parameter: string]: string } = {}

    // Match #[parameter-value]#{parameter-name}
    this.template.match(/#\[.*?\]#\{.*?\}/gu)?.forEach((match) => {
      const index = match.search('{')
      const parameterName = match.slice(index + 1, -1)
      const parameterValue = match.slice(2, index - 2)

      templateValues[parameterName] = parameterValue
    })

    return templateValues
  }

  /**
   * Returns the description as an array of strings.
   *
   * Example: "The node id was #[123]#{id} and it sent #[1234 XRP]#{amount}" => [
   * { value: "The node id was " },
   * { value: "123", parameterName: "id" },
   * { value: " and it sent " },
   * { value: "1234 XRP", parameterName: "amount" },
   * ].
   *
   * @returns The description as an array of strings.
   */
  public toArray(): Array<{
    readonly value: string
    readonly parameterName?: string
  }> {
    const template = this.getTemplate()
    const templateValues = this.getTemplateValues()

    // TODO: find a more "accurate" way to split?

    // Split on either curly brace.
    const templateAsArray = template
      .split(/#\{|\}/gu)
      .filter((str) => str !== '')

    return templateAsArray.map((maybeParameterName) => {
      const maybeParameterValue = templateValues[maybeParameterName]

      if (maybeParameterValue) {
        return {
          value: maybeParameterValue,
          parameterName: maybeParameterName,
        }
      }

      return {
        value: maybeParameterName,
      }
    })
  }
}
